import { useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { ref, onValue, update } from "firebase/database";
import { toast } from "react-toastify";
import { auth, database } from "../firebase";
import {
  getAuth,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from "firebase/auth";
import "../styles/dashboard.css"; // Optional if you use shared styles

const threshold = 10000;

const Rewards = () => {
  const [pointsEarned, setPointsEarned] = useState(0);
  const [pointsReview, setPointsReview] = useState(0);
  const [pointsRedeem, setPointsRedeem] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [password, setPassword] = useState("");
  const [pointsData, setPointsData] = useState();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        const userId = user.uid;
        const pointRef = ref(database, "Points/" + userId);

        onValue(pointRef, (snapshot) => {
          const data = snapshot.val();
          setPointsData(data);
          setPointsEarned(data?.point_available || 0);
          setPointsReview(data?.point_review || 0);
          setPointsRedeem(data?.point_redeemed || 0);
        });
      }
    });

    return () => unsubscribe();
  }, []);

  const handleRedeemClick = () => {
    if (pointsEarned >= threshold) {
      setShowModal(true);
    } else {
      toast.error("You need at least 10,000 points to redeem a reward.");
    }
  };

  const reverifyPassword = async (password) => {
    const auth = getAuth();
    const user = auth.currentUser;
    const credential = EmailAuthProvider.credential(user.email, password);
    await reauthenticateWithCredential(user, credential).catch((error) => {
      toast.error("Incorrect password. Please try again.");
      throw error;
    });
  };

  const handleVerifyPassword = async () => {
    if (!password.trim()) {
      toast.error("Please enter your password");
      return;
    }

    try {
      await reverifyPassword(password);
      setShowModal(false);
      setPassword("");
      toast.success("Check your registered email for the Amazon Gift Card!");

      const user = auth.currentUser;
      const rewardRef = ref(database, `Points/${user.uid}`);
      await update(rewardRef, {
        point_earned: pointsData?.point_earned || 0,
        point_redeemed: (pointsData?.point_redeemed || 0) + 10000,
        point_available: (pointsData?.point_available || 0) - 10000,
        point_expired: pointsData?.point_expired || 0,
        point_review: pointsData?.point_review || 0,
        point_rejected: pointsData?.point_rejected || 0,
      });
    } catch (e) {}
  };

  return (
    <div style={styles.wrapper}>
      <h3 style={styles.title}>Rewards Center</h3>

      <div style={styles.container}>
        <div style={styles.grid}>
          <div style={{ ...styles.card, borderColor: "#2979ff" }}>
            <h4 style={styles.cardTitle}>Points Available</h4>
            <p style={{ ...styles.points, color: "#2979ff" }}>{pointsEarned}</p>
          </div>

          <div style={{ ...styles.card, borderColor: "#ffa000" }}>
            <h4 style={styles.cardTitle}>Points in Review</h4>
            <p style={{ ...styles.points, color: "#ffa000" }}>{pointsReview}</p>
          </div>

          <div style={{ ...styles.card, borderColor: "#00bfa5" }}>
            <h4 style={styles.cardTitle}>Points Redeemed</h4>
            <p style={{ ...styles.points, color: "#00bfa5" }}>{pointsRedeem}</p>
          </div>
        </div>

        <p style={styles.note}>
          💡 <strong>Note:</strong> You can redeem your reward once you reach{" "}
          <strong>10,000</strong> points (100 points = $1).
        </p>

        <button style={styles.redeemButton} onClick={handleRedeemClick}>
          Redeem Amazon Gift Card
        </button>
      </div>

      {/* Modal */}
      {showModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalBox}>
            <h3 style={{ marginBottom: 12 }}>Verify Password</h3>
            <input
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
            />
            <div style={styles.modalActions}>
              <button style={styles.cancelBtn} onClick={() => setShowModal(false)}>
                Cancel
              </button>
              <button style={styles.verifyBtn} onClick={handleVerifyPassword}>
                Verify
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Rewards;

const styles = {
  wrapper: {
    padding: "40px 20px",
    minHeight: "100vh",
    background: "linear-gradient(to bottom right, #F3EBFF, #ffffff)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  title: {
    fontSize: 28,
    fontWeight: 800,
    background: "linear-gradient(to right, #8F27FF, #15007d)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    marginBottom: 30,
    textTransform: "uppercase",
    letterSpacing: 1,
    animation: "fadeIn 0.6s ease-in-out",
  },
  container: {
    background: "#fff",
    padding: 30,
    borderRadius: 16,
    maxWidth: 900,
    width: "100%",
    boxShadow: "0 10px 40px rgba(0,0,0,0.08)",
  },
  grid: {
    display: "flex",
    gap: 20,
    flexWrap: "wrap",
    justifyContent: "space-between",
  },
  card: {
    flex: 1,
    minWidth: 250,
    background: "#F9F6FF",
    padding: 20,
    borderRadius: 12,
    border: "2px solid",
    textAlign: "center",
    transition: "transform 0.3s",
    boxShadow: "0 6px 18px rgba(143, 39, 255, 0.1)",
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 10,
    color: "#200D41",
  },
  points: {
    fontSize: 28,
    fontWeight: 800,
  },
  note: {
    fontSize: 14,
    color: "#555",
    marginTop: 24,
    marginBottom: 16,
  },
  redeemButton: {
    backgroundColor: "#200D41",
    color: "#fff",
    padding: "12px 30px",
    fontSize: 16,
    fontWeight: 600,
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    transition: "all 0.3s ease-in-out",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 99,
  },
  modalBox: {
    background: "#fff",
    padding: 30,
    borderRadius: 12,
    width: "90%",
    maxWidth: 400,
    boxShadow: "0 8px 25px rgba(0,0,0,0.2)",
  },
  input: {
    width: "100%",
    padding: 10,
    borderRadius: 6,
    border: "1px solid #ccc",
    marginBottom: 20,
    fontSize: 14,
  },
  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
  },
  cancelBtn: {
    padding: "8px 16px",
    backgroundColor: "#ccc",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
  },
  verifyBtn: {
    padding: "8px 16px",
    backgroundColor: "#15007d",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
  },
};
